import React, { useEffect, useMemo, useState } from 'react';
import {
  createControlImportDraft,
  normalizeRosterOptions,
  validateControlImportDraft,
} from './utils/controlImportPreviewDraft';

const formatPercent = (score) => (
  typeof score === 'number' ? `${Math.round(score * 100)}%` : 'N/A'
);

const NUMERIC_COLUMNS = [
  ['score', 'Score'],
  ['kills', 'K'],
  ['deaths', 'D'],
  ['assists', 'A'],
  ['impact', 'Impact'],
  ['damage', 'Damage'],
  ['captureKills', 'Capture Kills'],
  ['operatorValue', 'Operator Value'],
];

const countUnresolvedRecords = (group = {}) => (
  Object.values(group).reduce((total, records) => total + (records?.length || 0), 0)
);

const ImportPreview = ({
  activePlayers = [],
  operators = [],
  mergedControlResult = null,
  onApplyControlImport,
  isProcessing = false,
}) => {
  const rosterOptions = useMemo(() => normalizeRosterOptions(activePlayers), [activePlayers]);
  const [draftPlayers, setDraftPlayers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const [showReviewDetails, setShowReviewDetails] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');

  useEffect(() => {
    setDraftPlayers(createControlImportDraft({
      mergedControlResult,
      activePlayers,
    }));
    setExpandedRows(new Set());
    setShowReviewDetails(false);
    setApplyMessage('');
  }, [mergedControlResult, activePlayers]);

  const validation = useMemo(() => validateControlImportDraft(draftPlayers), [draftPlayers]);
  const unresolvedCount = (
    countUnresolvedRecords(mergedControlResult?.unmatched) +
    countUnresolvedRecords(mergedControlResult?.ambiguous)
  );
  const hasMergedRows = draftPlayers.length > 0;

  const updateDraftRow = (rowId, fieldKey, value) => {
    setDraftPlayers((current) => current.map((row) => (
      row.rowId === rowId ? { ...row, [fieldKey]: value } : row
    )));
  };

  const updatePlayerSelection = (rowId, playerId) => {
    const selected = rosterOptions.find((option) => String(option.playerId) === String(playerId));
    setDraftPlayers((current) => current.map((row) => (
      row.rowId === rowId
        ? {
          ...row,
          playerId: selected?.playerId || null,
          playerName: selected?.playerName || '',
        }
        : row
    )));
  };

  const toggleRow = (rowId) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const handleApply = () => {
    if (!validation.isValid || isProcessing || !onApplyControlImport) return;
    const applied = onApplyControlImport(draftPlayers);
    if (applied !== false) {
      setApplyMessage(`${draftPlayers.length} players applied to the Player Stats form.`);
    }
  };

  if (!mergedControlResult) {
    return (
      <div className="ocr-preview-placeholder">
        <h4>Control Import Preview</h4>
        <p>Process Control screenshots to review editable merged rows before anything reaches the form.</p>
        <div className="ocr-preview-meta">
          <span>{activePlayers.length} roster players available</span>
          <span>{operators.length} operators available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ocr-import-preview-panel">
      <div className="ocr-import-preview-header">
        <div>
          <h4>Control Import Preview</h4>
          <p>Review OCR values, choose roster players, and select operators before the future apply step.</p>
        </div>
        <div className={`ocr-import-status ${validation.isValid ? 'is-ready' : 'needs-review'}`}>
          <strong>{validation.isValid ? 'Ready to Apply' : 'Review Required'}</strong>
          <span>
            {validation.summary.mappedPlayers} / {validation.summary.totalRows} players mapped
          </span>
          <span>{validation.summary.fieldsNeedingAttention} fields need attention</span>
        </div>
      </div>

      {unresolvedCount > 0 && (
        <div className="ocr-review-needed">
          <button
            type="button"
            onClick={() => setShowReviewDetails((current) => !current)}
          >
            OCR Review Needed
          </button>
          <span>{mergedControlResult.unmatched.impact.length} unmatched Impact</span>
          <span>{mergedControlResult.unmatched.prop.length} unmatched Prop</span>
          <span>{mergedControlResult.ambiguous.impact.length} ambiguous Impact</span>
          <span>{mergedControlResult.ambiguous.prop.length} ambiguous Prop</span>
        </div>
      )}

      {showReviewDetails && (
        <div className="ocr-review-details">
          {['impact', 'prop'].map((type) => (
            <React.Fragment key={type}>
              {mergedControlResult.unmatched[type].map((record, index) => (
                <p key={`unmatched-${type}-${record.sourceRowId || index}`}>
                  Unmatched {type}: {record.detectedName || 'Name not detected'}
                </p>
              ))}
              {mergedControlResult.ambiguous[type].map((record, index) => (
                <p key={`ambiguous-${type}-${record.sourceRowId || index}`}>
                  Ambiguous {type}: {record.detectedName || 'Name not detected'}
                </p>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}

      {!hasMergedRows ? (
        <div className="ocr-preview-empty">
          Match Details did not produce anchored player rows for the preview.
        </div>
      ) : (
        <div className="ocr-import-table-wrap">
          <table className="ocr-import-table">
            <thead>
              <tr>
                <th>Detected</th>
                <th>Player</th>
                <th>Operator</th>
                {NUMERIC_COLUMNS.map(([, label]) => (
                  <th key={label}>{label}</th>
                ))}
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {draftPlayers.map((row) => {
                const rowErrors = validation.errorsByRow[row.rowId] || {};
                const isExpanded = expandedRows.has(row.rowId);

                return (
                  <React.Fragment key={row.rowId}>
                    <tr>
                      <td>
                        <div className="ocr-detected-name">
                          <strong>{row.detectedName || 'Unread'}</strong>
                          {row._rosterSuggestion && (
                            <span>
                              {row._rosterSuggestion.type === 'ambiguous'
                                ? 'Ambiguous roster suggestion'
                                : `${row._rosterSuggestion.type} roster suggestion ${formatPercent(row._rosterSuggestion.score)}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={rowErrors.playerId ? 'has-error' : ''}>
                        <label className="sr-only" htmlFor={`${row.rowId}-player`}>Player</label>
                        <select
                          id={`${row.rowId}-player`}
                          aria-label={`Player for ${row.detectedName || 'OCR row'}`}
                          value={row.playerId || ''}
                          onChange={(event) => updatePlayerSelection(row.rowId, event.target.value)}
                        >
                          <option value="">Select Player</option>
                          {rosterOptions.map((player) => (
                            <option key={player.playerId} value={player.playerId}>
                              {player.playerName}
                            </option>
                          ))}
                        </select>
                        {rowErrors.playerId && <small>{rowErrors.playerId}</small>}
                      </td>
                      <td className={rowErrors.operator ? 'has-error' : ''}>
                        <label className="sr-only" htmlFor={`${row.rowId}-operator`}>Operator</label>
                        <select
                          id={`${row.rowId}-operator`}
                          aria-label={`Operator for ${row.detectedName || 'OCR row'}`}
                          value={row.operator}
                          onChange={(event) => updateDraftRow(row.rowId, 'operator', event.target.value)}
                        >
                          <option value="">Select Operator</option>
                          {operators.map((operator) => (
                            <option key={operator} value={operator}>
                              {operator}
                            </option>
                          ))}
                        </select>
                        {rowErrors.operator && <small>{rowErrors.operator}</small>}
                      </td>
                      {NUMERIC_COLUMNS.map(([fieldKey, label]) => (
                        <td key={fieldKey} className={rowErrors[fieldKey] ? 'has-error' : ''}>
                          <label className="sr-only" htmlFor={`${row.rowId}-${fieldKey}`}>
                            {label}
                          </label>
                          <input
                            id={`${row.rowId}-${fieldKey}`}
                            aria-label={`${label} for ${row.detectedName || 'OCR row'}`}
                            type="number"
                            min="0"
                            value={row[fieldKey]}
                            onChange={(event) => updateDraftRow(row.rowId, fieldKey, event.target.value)}
                          />
                          {rowErrors[fieldKey] && <small>{rowErrors[fieldKey]}</small>}
                        </td>
                      ))}
                      <td>
        <button
          type="button"
          className="ocr-row-detail-toggle"
          onClick={() => toggleRow(row.rowId)}
                        >
                          {isExpanded ? 'Hide' : 'Show'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="ocr-import-detail-row">
                        <td colSpan={12}>
                          <div className="ocr-import-row-details">
                            <div>
                              <strong>Match</strong>
                              <span>{row._sources.matchDetails?.detectedName || 'N/A'}</span>
                            </div>
                            <div>
                              <strong>Impact</strong>
                              <span>
                                {row._sources.impactDetails
                                  ? `${row._sources.impactDetails.detectedName} - ${row._sources.impactDetails.matchType.toUpperCase()} ${formatPercent(row._sources.impactDetails.matchScore)}`
                                  : 'Not matched'}
                              </span>
                            </div>
                            <div>
                              <strong>Prop</strong>
                              <span>
                                {row._sources.propDetails?.length
                                  ? row._sources.propDetails.map((source) => `${source.detectedName} (${source.sourceScreenshotName || 'Prop'})`).join(', ')
                                  : 'Not matched'}
                              </span>
                            </div>
                            {row._propValueCandidates.length > 0 && (
                              <div>
                                <strong>Prop Conflict</strong>
                                <span>{row._propValueCandidates.join(' / ')}</span>
                              </div>
                            )}
                            {row._warnings.length > 0 && (
                              <div className="ocr-import-row-warning-list">
                                <strong>Warnings</strong>
                                {row._warnings.map((warning) => (
                                  <span key={warning}>{warning}</span>
                                ))}
                              </div>
                            )}
                            {row._rosterSuggestion?.type === 'ambiguous' && (
                              <div className="ocr-import-row-warning-list">
                                <strong>Roster Candidates</strong>
                                {row._rosterSuggestion.candidates.map((candidate) => (
                                  <span key={candidate.playerId}>
                                    {candidate.playerName} - {formatPercent(candidate.score)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="ocr-import-preview-footer">
        <span>
          {applyMessage || (
            validation.isValid
              ? 'Preview is complete. Apply will fill the form only; submission still uses the normal submit button.'
              : 'Complete highlighted fields before applying to the form.'
          )}
        </span>
        <button
          type="button"
          disabled={!validation.isValid || isProcessing || !onApplyControlImport}
          onClick={handleApply}
        >
          Apply to Player Stats Form
        </button>
      </div>
    </div>
  );
};

export default ImportPreview;
